import { clearSession, getToken } from "./auth";
// Generated schema — refresh with `npm run gen:api`. The re-export below makes
// `import { type ApiPaths } from "./api"` work in consumer code that wants
// per-endpoint typing without taking a direct dependency on the schema file.
import type { paths as ApiPaths } from "./api-schema";
import type {
  AgentEvent,
  AttachTaskRequest,
  AutonomyLevel,
  BatchConfirmResult,
  BulkAttachTaskRequest,
  ChatMessage,
  ChatRole,
  CheckpointData,
  ConfirmDoneResult,
  CostEstimate,
  DecisionCard,
  Idea,
  ModelChoice,
  Project,
  ProjectSkill,
  SearchResponse,
  SlashCommandResult,
  SpawnedProposal,
  Task,
  TaskCreated,
  TaskHardDeleted,
  TaskKind,
  TaskPriority,
  TaskProposal,
  TaskStatus,
  Track,
  TrackPlan,
  TrackProposal,
  TrackStatus,
  Verification,
} from "./types";

const BASE = "/api";

class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

/** Body that pushes a close-out past the gate; needs a written reason. */
export type CloseOutOverride = { override: boolean; reason: string };

/** The merge gate's blocking reasons if ``e`` is its 409, else null. */
export function closeOutGateReasons(e: unknown): string | null {
  if (!(e instanceof ApiError) || e.status !== 409) return null;
  const detail = (e.body as { detail?: unknown } | null)?.detail as
    | { gate?: string; blocked?: Record<string, string[]> }
    | undefined;
  if (!detail || detail.gate !== "verification" || !detail.blocked) return null;
  return Object.values(detail.blocked).flat().join("\n");
}

/**
 * Run a close-out call; if the gate blocks it, ask for an override reason
 * and retry once with it. Cancelling (or an empty reason) keeps the block.
 */
export async function withCloseOutOverride<T>(
  run: (override?: CloseOutOverride) => Promise<T>,
): Promise<T> {
  try {
    return await run();
  } catch (e) {
    const reasons = closeOutGateReasons(e);
    if (!reasons) throw e;
    const reason = window.prompt(
      `Close-out blocked:\n${reasons}\n\nType a reason to override, or cancel.`,
    );
    if (!reason || !reason.trim()) throw e;
    return run({ override: true, reason: reason.trim() });
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set("Content-Type", "application/json");
  const token = getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const resp = await fetch(`${BASE}${path}`, { ...options, headers });
  if (resp.status === 401) {
    clearSession();
    if (!path.startsWith("/auth/login")) {
      // Navigation is async; stop here so we don't fall through to parse the
      // body and throw a stray 401 that callers (react-query) may flash before
      // the reload lands. Returns a never-resolving promise so the page swaps.
      window.location.reload();
      return new Promise<T>(() => {});
    }
  }
  if (resp.status === 204) {
    return undefined as unknown as T;
  }
  const text = await resp.text();
  const body = text ? safeParseJSON(text) : null;
  if (!resp.ok) {
    const message =
      (body && typeof body === "object" && "detail" in body
        ? (typeof (body as { detail: unknown }).detail === "string"
            ? String((body as { detail: unknown }).detail)
            : JSON.stringify((body as { detail: unknown }).detail))
        : resp.statusText) || "request failed";
    throw new ApiError(message, resp.status, body);
  }
  return body as T;
}

function safeParseJSON(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

// ----- Auth ---------------------------------------------------------------

export async function login(
  username: string,
  password: string,
): Promise<{ token: string; username: string }> {
  return request("/auth/login", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  });
}

// ----- Projects -----------------------------------------------------------

export interface ProjectCreate {
  name: string;
  description?: string | null;
  host: string;
  ssh_user: string;
  ssh_key_path: string;
  ssh_key_passphrase?: string | null;
  repo_path: string;
  cli_binary?: string;
  cli_flags?: string;
  default_autonomy_level?: AutonomyLevel;
  default_model?: string | null;
  worker_model?: string | null;
  fallback_models?: string[];
  use_worktrees?: boolean;
  secret_scan_enabled?: boolean;
  manager_loop_enabled?: boolean;
  worker_loop_enabled?: boolean;
  security_scan_enabled?: boolean;
  max_concurrent_auto?: number;
  auto_merge?: boolean;
  allowed_automerge_types?: string[];
  triage_requires_confirmation?: boolean;
  risk_autonomy_policy?: Record<string, unknown>;
  quota_pause_threshold?: number;
  auto_token_window_budget?: number | null;
  test_command?: string | null;
  agent_user?: string | null;
}

export const projects = {
  list: (includeInactive = false) =>
    request<Project[]>(
      `/projects${includeInactive ? "?include_inactive=true" : ""}`,
    ),
  get: (id: string) => request<Project>(`/projects/${id}`),
  create: (data: ProjectCreate) =>
    request<Project>("/projects", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  update: (
    id: string,
    data: Partial<ProjectCreate> & { active?: boolean },
  ) =>
    request<Project>(`/projects/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  deactivate: (id: string) =>
    request<void>(`/projects/${id}`, { method: "DELETE" }),
  hardDelete: (id: string) =>
    request<void>(`/projects/${id}?hard=true`, { method: "DELETE" }),
  restore: (id: string) =>
    request<Project>(`/projects/${id}/restore`, { method: "POST" }),
  testSsh: (id: string) =>
    request<{ ok: boolean; message: string }>(
      `/projects/${id}/test-ssh`,
      { method: "POST" },
    ),
  testSshConfig: (data: ProjectCreate) =>
    request<{ ok: boolean; message: string }>("/projects/test-ssh-config", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  syncDocs: (id: string) =>
    request<{ results: Record<string, boolean> }>(
      `/projects/${id}/sync-docs`,
      { method: "POST" },
    ),
  reorder: (ids: string[]) =>
    request<Project[]>("/projects/reorder", {
      method: "POST",
      body: JSON.stringify({ project_ids: ids }),
    }),
  estimateCost: (id: string, prompt: string) =>
    request<CostEstimate>(`/projects/${id}/estimate-cost`, {
      method: "POST",
      body: JSON.stringify({ prompt }),
    }),
  skills: (id: string) => request<ProjectSkill[]>(`/projects/${id}/skills`),
};

// ----- Tasks --------------------------------------------------------------

/** Task size: picks the token budget and, with tier routing, the model. */
export type TaskTier = "tiny" | "normal" | "large";
export const TASK_TIERS: TaskTier[] = ["tiny", "normal", "large"];

export interface TaskCreate {
  project_id: string;
  title: string;
  description?: string | null;
  prompt?: string | null;
  status?: TaskStatus;
  kind?: TaskKind;
  autonomy_level?: AutonomyLevel;
  model?: string | null;
  priority?: TaskPriority | null;
  spec_mode?: boolean;
  tier?: TaskTier;
  acceptance_criteria?: string | null;
}

export const models = {
  list: () =>
    request<{ models: ModelChoice[]; refreshed_at: string | null }>("/models"),
  add: (m: {
    model_id: string;
    label: string;
    cli_binary: string;
    auth_method?: string | null;
    is_alias?: boolean;
  }) =>
    request<{ models: ModelChoice[]; refreshed_at: string | null }>("/models", {
      method: "POST",
      body: JSON.stringify(m),
    }),
  remove: (cli_binary: string, model_id: string) =>
    request<{ models: ModelChoice[]; refreshed_at: string | null }>(
      `/models/${encodeURIComponent(cli_binary)}/${encodeURIComponent(model_id)}`,
      { method: "DELETE" },
    ),
  refresh: () =>
    request<{ reconciled: number; models: ModelChoice[]; refreshed_at: string | null }>(
      "/models/refresh",
      {
        method: "POST",
      },
    ),
};

export const tasks = {
  get: (id: string) => request<Task>(`/tasks/${id}`),
  create: (data: TaskCreate) =>
    request<TaskCreated>("/tasks", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  update: (id: string, data: Partial<TaskCreate>) =>
    request<Task>(`/tasks/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  dispatch: (id: string) =>
    request<Task>(`/tasks/${id}/dispatch`, { method: "POST" }),
  // Run an interrupted task again (server caps at MAX_TASK_ATTEMPTS).
  retry: (id: string) =>
    request<Task>(`/tasks/${id}/retry`, { method: "POST" }),
  triage: (id: string) =>
    request<Task>(`/tasks/${id}/triage`, { method: "POST" }),
  route: (id: string, triage_status: "agent_ready" | "needs_human") =>
    request<Task>(`/tasks/${id}/route`, {
      method: "POST",
      body: JSON.stringify({ triage_status }),
    }),
  respond: (id: string, response: string) =>
    request<Task>(`/tasks/${id}/respond`, {
      method: "POST",
      body: JSON.stringify({ response }),
    }),
  cancel: (id: string) =>
    request<Task>(`/tasks/${id}/cancel`, { method: "POST" }),
  decisionCard: (id: string) => request<DecisionCard>(`/tasks/${id}/decision-card`),
  // Answer a decision card; a stale ``attempt`` is a 409 "Already handled".
  decide: (
    id: string,
    body: { action: string; attempt: string; note?: string; reason?: string },
  ) =>
    request<{ message: string; follow_up_task_id: string | null }>(
      `/tasks/${id}/decide`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  events: (id: string, after?: number, limit?: number) => {
    const q = new URLSearchParams();
    if (typeof after === "number") q.set("after", String(after));
    if (typeof limit === "number") q.set("limit", String(limit));
    const suffix = q.toString() ? `?${q.toString()}` : "";
    return request<{ task_id: string; events: AgentEvent[] }>(
      `/tasks/${id}/events${suffix}`,
    );
  },
  approveSpawned: (id: string, proposals: SpawnedProposal[], indices: number[]) =>
    request<{ pending: SpawnedProposal[]; materialized: string[] }>(
      `/tasks/${id}/spawned-proposals/approve`,
      { method: "POST", body: JSON.stringify({ proposals, indices }) },
    ),
  rejectSpawned: (id: string, indices: number[]) =>
    request<{ pending: SpawnedProposal[]; materialized: string[] }>(
      `/tasks/${id}/spawned-proposals/reject`,
      { method: "POST", body: JSON.stringify({ indices }) },
    ),
  // Keep a proposal as a parked idea: visible, never a task until accepted.
  parkSpawned: (id: string, indices: number[]) =>
    request<{ pending: SpawnedProposal[]; materialized: string[] }>(
      `/tasks/${id}/spawned-proposals/park`,
      { method: "POST", body: JSON.stringify({ indices }) },
    ),
  // Every proposal still waiting on the operator, across tasks.
  ideas: (projectId?: string | null) =>
    request<Idea[]>(`/tasks/ideas${projectId ? `?project_id=${projectId}` : ""}`),
  // The task's life in plain text: origin, rounds, decisions, outcome, cost.
  story: (id: string) => request<{ text: string }>(`/tasks/${id}/story`),
  verification: (id: string) => request<Verification>(`/tasks/${id}/verification`),
  confirmDone: (id: string, override?: CloseOutOverride) =>
    request<ConfirmDoneResult>(`/tasks/${id}/confirm-done`, {
      method: "POST",
      body: override ? JSON.stringify(override) : undefined,
    }),
  // Operator "Confirm all" for a whole track — confirms each open task in one
  // explicit action (the shared branch merges once).
  confirmTrack: (trackId: string, override?: CloseOutOverride) =>
    request<ConfirmDoneResult>(`/tasks/confirm-track/${trackId}`, {
      method: "POST",
      body: override ? JSON.stringify(override) : undefined,
    }),
  confirmDoneBatch: (taskIds: string[], override?: CloseOutOverride) =>
    request<BatchConfirmResult>(`/tasks/confirm-done-batch`, {
      method: "POST",
      body: JSON.stringify({ task_ids: taskIds, ...(override ?? {}) }),
    }),
  sendBack: (id: string, feedback: string) =>
    request<Task>(`/tasks/${id}/send-back`, {
      method: "POST",
      body: JSON.stringify({ feedback }),
    }),
  archive: (id: string) =>
    request<void>(`/tasks/${id}`, { method: "DELETE" }),
  hardDelete: (id: string) =>
    request<TaskHardDeleted>(`/tasks/${id}?hard=true`, { method: "DELETE" }),
};

// ----- Kanban -------------------------------------------------------------

export interface KanbanColumn {
  status: TaskStatus;
  tasks: Task[];
}

export interface KanbanBoard {
  columns: KanbanColumn[];
}

export const kanban = {
  board: (project_id?: string, include_archived = false) => {
    const params = new URLSearchParams();
    if (project_id) params.set("project_id", project_id);
    if (include_archived) params.set("include_archived", "true");
    const q = params.toString();
    return request<KanbanBoard>(`/kanban${q ? `?${q}` : ""}`);
  },
  move: (id: string, status: TaskStatus) =>
    request<Task>(`/kanban/${id}/move`, {
      method: "POST",
      body: JSON.stringify({ status }),
    }),
};

// ----- Dashboard ----------------------------------------------------------

export const dashboard = {
  live: () =>
    request<{ in_progress: number; needs_input: number; total_live: number }>(
      "/dashboard/live",
    ),
};

// ----- Usage quota meter --------------------------------------------------

export interface QuotaWindow {
  utilization: number;
  reset_at: string;
  status: string;
}

export interface QuotaSnapshot {
  available: boolean;
  stale: boolean;
  fetched_at: string;
  five_hour: QuotaWindow | null;
  seven_day: QuotaWindow | null;
  overage_disabled_reason: string | null;
  reason: string | null;
}

export const usage = {
  quota: () => request<QuotaSnapshot>("/usage/quota"),
};

// ----- Autonomous loops ---------------------------------------------------

export interface LoopProjectStatus {
  project_id: string;
  name: string;
  manager_loop_enabled: boolean;
  worker_loop_enabled: boolean;
  security_scan_enabled: boolean;
  triage_requires_confirmation: boolean;
  max_concurrent_auto: number;
  running: number;
  agent_ready: number;
  needs_human: number;
  untriaged: number;
  token_window_budget: number | null;
  token_window_spent: number;
}

export interface LoopStatus {
  master_enabled: boolean; // effective: env_enabled && runtime_enabled
  env_enabled: boolean; // the AUTONOMOUS_LOOPS_ENABLED env ceiling
  runtime_enabled: boolean; // the in-app runtime switch
  quota: QuotaSnapshot;
  projects: LoopProjectStatus[];
}

export interface LoopToggleResult {
  master_enabled: boolean;
  env_enabled: boolean;
  runtime_enabled: boolean;
}

export interface LoopRun {
  id: string;
  kind: "manager" | "worker" | "security";
  project_id: string | null;
  started_at: string;
  finished_at: string | null;
  examined: number;
  acted: number;
  actions: Record<string, unknown>[];
  quota_snapshot: Record<string, unknown> | null;
  error: string | null;
}

export const loops = {
  status: () => request<LoopStatus>("/loops/status"),
  toggle: (enabled: boolean) =>
    request<LoopToggleResult>("/loops/toggle", {
      method: "POST",
      body: JSON.stringify({ enabled }),
    }),
  triageNow: (projectId: string) =>
    request<{ examined: number; acted: number }>(
      `/loops/triage/${projectId}`,
      { method: "POST" },
    ),
  runs: (params?: {
    kind?: "manager" | "worker" | "security";
    project_id?: string;
    limit?: number;
    nonempty?: boolean;
  }) => {
    const q = new URLSearchParams();
    if (params?.kind) q.set("kind", params.kind);
    if (params?.project_id) q.set("project_id", params.project_id);
    if (params?.limit) q.set("limit", String(params.limit));
    if (params?.nonempty) q.set("nonempty", "true");
    const suffix = q.toString() ? `?${q.toString()}` : "";
    return request<LoopRun[]>(`/loops/runs${suffix}`);
  },
};

// ----- Chat ---------------------------------------------------------------

export type ChatStreamEvent =
  | { type: "delta"; text: string }
  | {
      type: "done";
      content: string;
      thread_id: string;
      task_proposal: TaskProposal | null;
      track_proposal: TrackProposal | null;
    };

// ----- Security scan ------------------------------------------------------

export type VulnSeverity = "critical" | "high" | "medium" | "low" | "unknown";

export interface SecurityHostProject {
  project_id: string;
  name: string;
  active: boolean;
  security_scan_enabled: boolean;
}

// One server: its OS, images and apps, plus the repos of every project on it.
export interface SecurityHostSummary {
  host: string;
  projects: SecurityHostProject[]; // the first one lends the host its login and Telegram topic
  open_critical: number;
  open_high: number;
  open_medium: number;
  open_low: number;
  open_unknown: number;
  no_fix: number; // open, and no fixed version exists yet
  exploited: number; // open, on CISA's Known Exploited Vulnerabilities list
  open_cves: number; // distinct CVE ids (one CVE shows once per package/image)
  patchable: string[]; // OS packages "Patch now" would upgrade
  fixable_packages: string[]; // OS packages "Fix all" would upgrade (any severity)
  fixable_places: number; // images/lockfiles "Fix all" would open a task for
  last_scan_at: string | null;
  last_error: string | null;
}

export interface SecuritySummary {
  enabled: boolean; // the SECURITY_SCAN_ENABLED env switch
  scanning_host: string | null;
  patching_hosts: string[]; // a "Patch now" is running there
  hosts: SecurityHostSummary[];
}

export interface VulnFinding {
  id: string;
  project_id: string | null; // a repo finding's project; null for the host's OS, images and apps
  host: string;
  surface: "os" | "image" | "repo" | "app";
  target: string;
  package: string;
  installed_version: string;
  fixed_version: string | null;
  vuln_id: string;
  severity: VulnSeverity;
  title: string | null;
  url: string | null;
  exploited: boolean;
  status: "open" | "fixed" | "snoozed" | "wontfix";
  first_seen: string;
  last_seen: string;
  resolved_at: string | null;
  task_id: string | null; // the board card "Make task" opened
}

// Open findings grouped by the one action that clears them.
export type FixUnitKind = "apt" | "kernel" | "pull" | "task" | "manual" | "wait";

export interface FixUnit {
  host: string;
  project_id: string | null; // a repo unit's project
  key: string;
  kind: FixUnitKind;
  title: string;
  detail: string;
  findings: number;
  cves: number;
  critical: number;
  high: number;
  exploited: number;
  targets: string[];
  packages: string[];
  task_id: string | null; // a task unit's live board card
  confirm: boolean; // removes a fallback (old kernel, running image): ask first
  sample: VulnFinding[]; // the worst findings, up to 50
}

export const security = {
  summary: () => request<SecuritySummary>("/security/summary"),
  findings: (params?: {
    host?: string;
    status?: "open" | "fixed" | "snoozed" | "wontfix" | "all";
    limit?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.host) q.set("host", params.host);
    if (params?.status) q.set("status", params.status);
    if (params?.limit) q.set("limit", String(params.limit));
    const suffix = q.toString() ? `?${q.toString()}` : "";
    return request<VulnFinding[]>(`/security/findings${suffix}`);
  },
  scanNow: (host: string) =>
    request<{ started: boolean }>(`/security/scan/${encodeURIComponent(host)}`, { method: "POST" }),
  patchNow: (host: string) =>
    request<{ message: string }>(`/security/patch/${encodeURIComponent(host)}`, { method: "POST" }),
  units: (host?: string) =>
    request<FixUnit[]>(`/security/units${host ? `?host=${encodeURIComponent(host)}` : ""}`),
  runUnit: (host: string, key: string, confirmed = false) =>
    request<{ outcome: "started" | "task"; message: string }>(
      `/security/units/${encodeURIComponent(host)}/run`,
      { method: "POST", body: JSON.stringify({ key, confirmed }) },
    ),
  fixAll: (host: string) =>
    request<{ message: string; patch: string | null; tasks_added: number; tasks_existing: number }>(
      `/security/fix/${encodeURIComponent(host)}`,
      { method: "POST" },
    ),
  makeTask: (findingId: string) =>
    request<{ task_id: string; message: string }>(`/security/findings/${findingId}/task`, {
      method: "POST",
    }),
};

export const chat = {

  /**
   * Streaming variant. Yields SSE events from POST /chat/stream.
   * The caller iterates an AsyncGenerator of ChatStreamEvent:
   *   - "delta" events carry incremental text chunks
   *   - the final "done" event carries the full content, thread_id, and proposals
   */
  async *sendStream(params: {
    message: string;
    project_id?: string;
    thread_id?: string;
    slash_command?: string;
    model?: string;
  }): AsyncGenerator<ChatStreamEvent> {
    const token = getToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
    };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const resp = await fetch(`${BASE}/chat/stream`, {
      method: "POST",
      headers,
      body: JSON.stringify(params),
    });

    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Chat stream failed: ${resp.status} ${text}`);
    }

    const reader = resp.body!.getReader();
    const decoder = new TextDecoder();
    let buf = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });

      // SSE events are separated by double newlines
      const parts = buf.split("\n\n");
      buf = parts.pop() ?? "";

      for (const part of parts) {
        const line = part.trim();
        if (!line.startsWith("data: ")) continue;
        try {
          const event = JSON.parse(line.slice("data: ".length)) as ChatStreamEvent;
          yield event;
        } catch {
          // malformed line — skip
        }
      }
    }

    // Flush remaining buffer
    if (buf.trim().startsWith("data: ")) {
      try {
        const event = JSON.parse(buf.trim().slice("data: ".length)) as ChatStreamEvent;
        yield event;
      } catch {
        // ignore
      }
    }
  },
  listThreads: (project_id?: string) => {
    const q = project_id ? `?project_id=${project_id}` : "";
    return request<import("./types").ChatThreadSummary[]>(`/chat/threads${q}`);
  },
  searchThreads: (query: string, project_id?: string) => {
    const params = new URLSearchParams({ q: query });
    if (project_id) params.set("project_id", project_id);
    return request<import("./types").ChatThreadSummary[]>(
      `/chat/threads/search?${params.toString()}`,
    );
  },
  getThread: (id: string) =>
    request<import("./types").ChatThreadDetail>(`/chat/threads/${id}`),
  renameThread: (id: string, title: string) =>
    request<import("./types").ChatThreadSummary>(`/chat/threads/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ title }),
    }),
  deleteThread: (id: string) =>
    request<void>(`/chat/threads/${id}`, { method: "DELETE" }),
  acceptProposal: (proposal: TaskProposal, dispatch = false) =>
    request<Task>("/chat/accept-proposal", {
      method: "POST",
      body: JSON.stringify({ proposal, dispatch }),
    }),
  acceptTrackProposal: (proposal: TrackProposal, dispatch = false) =>
    request<Track>("/chat/accept-track-proposal", {
      method: "POST",
      body: JSON.stringify({ proposal, dispatch }),
    }),
};

// ----- Tracks -------------------------------------------------------------

export interface TrackCreate {
  project_id: string;
  title: string;
  description?: string;
  plan: TrackPlan;
  models?: Record<string, string>;
  autonomy_level?: AutonomyLevel;
  status?: TrackStatus;
  parallel?: boolean;
  shared_session?: boolean;
}

export const tracks = {
  list: (project_id?: string) => {
    const q = project_id ? `?project_id=${project_id}` : "";
    return request<Track[]>(`/tracks${q}`);
  },
  get: (id: string) => request<Track>(`/tracks/${id}`),
  getTasks: (id: string) => request<Task[]>(`/tracks/${id}/tasks`),
  attachableTasks: (id: string) =>
    request<Task[]>(`/tracks/${id}/attachable-tasks`),
  attachTask: (trackId: string, taskId: string, body: AttachTaskRequest) =>
    request<Task>(`/tracks/${trackId}/tasks/${taskId}`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  attachTasksBulk: (trackId: string, body: BulkAttachTaskRequest) =>
    request<Task[]>(`/tracks/${trackId}/tasks/bulk`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  detachTask: (trackId: string, taskId: string) =>
    request<Task>(`/tracks/${trackId}/tasks/${taskId}`, { method: "DELETE" }),
  create: (data: TrackCreate) =>
    request<Track>("/tracks", { method: "POST", body: JSON.stringify(data) }),
  update: (id: string, data: Partial<TrackCreate>) =>
    request<Track>(`/tracks/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  dispatch: (id: string) =>
    request<Track>(`/tracks/${id}/dispatch`, { method: "POST" }),
  cancel: (id: string) =>
    request<Track>(`/tracks/${id}/cancel`, { method: "POST" }),
  estimate: (proposal: TrackProposal) =>
    request<CostEstimate>("/tracks/estimate", {
      method: "POST",
      body: JSON.stringify(proposal),
    }),
};

// ----- Slash commands -----------------------------------------------------
// The Slash *view* (registry tab) was removed, but the backend stays: it
// powers the new-task skill picker and Chat's inline /commands (slash.invoke).

export const slash = {
  invoke: (data: {
    name: string;
    args?: string;
    project_id?: string;
    task_id?: string;
  }) =>
    request<SlashCommandResult>("/slash", {
      method: "POST",
      body: JSON.stringify(data),
    }),
};

// ----- Search -------------------------------------------------------------

export const search = {
  query: (params: {
    q: string;
    project_id?: string;
    status?: TaskStatus;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    qs.set("q", params.q);
    if (params.project_id) qs.set("project_id", params.project_id);
    if (params.status) qs.set("status", params.status);
    if (params.limit) qs.set("limit", String(params.limit));
    return request<SearchResponse>(`/search?${qs.toString()}`);
  },
};

// ----- Re-export types ----------------------------------------------------

export type {
  CheckpointData,
  ChatMessage,
  ChatRole,
  Project,
  Task,
  TaskProposal,
  TaskStatus,
  Track,
  TrackProposal,
};

export type { ApiPaths };
